/**
 * Domain types for Create Chat functionality
 * These types are independent of GraphQL and represent the business domain
 */

// Base types for select options
export interface BaseSelectOption {
  value: string;
  label: string;
}

// Chat type options (rename to avoid conflict with existing ChatType)
export enum CreateChatType {
  ORDERING = 'ordering',
  SUPPORT = 'support',
}

export interface ChatTypeOption extends BaseSelectOption {
  value: CreateChatType;
}

// Created context options
export enum CreatedContext {
  ADMIN = 'chatCreatedFromAdmin',
  BUYER_CONCIERGE = 'chatCreatedFromBuyerConcierge',
  CHOCO_AI = 'chatCreatedFromChocoAi',
  ADD_ANOTHER_BUSINESS = 'addAnotherBusiness',
  BUYER_ADD_SUPPLIER = 'buyerAddSupplier',
  BUYER_ADDS_VERIFIED_SUPPLIER_NEW_CUSTOMER = 'buyerAddsVerifiedSupplierNewCustomer',
  BUYER_REQUESTS_SUPPLIER_EXISTING_CUSTOMER = 'buyerRequestsSupplierExistingCustomer',
  BUYER_REQUESTS_SUPPLIER_NEW_CUSTOMER = 'buyerRequestsSupplierNewCustomer',
  CUSTOMER_MASS_ACCOUNT_CREATOR = 'customerMassAccountCreator',
  INVITE_LINK = 'inviteLink',
  SUPPLIER_INVITES_BUYER = 'supplierInvitesBuyer',
}

export interface CreatedContextOption extends BaseSelectOption {
  value: CreatedContext;
}

// Order preference types
export enum OrderPreferenceType {
  WHATSAPP = 'WHATSAPP',
  SMS = 'SMS',
  FAX = 'FAX',
  EMAIL = 'EMAIL',
}

export interface OrderPreference {
  type: OrderPreferenceType;
  value: string;
}

// User metadata types
export interface UserMetadata {
  name?: string | null;
  supplierId?: string | null;
  phone?: string | null;
  email?: string | null;
  orderPreferences?: OrderPreference[];
  customerNumberMandatory?: boolean | null;
  supplier?: boolean;
  referenceId?: string | null;
}

export interface BuyerMetadata {
  fullAddress: string;
  name?: string | null;
  referenceId?: string | null;
}

// Select option types with metadata
export interface UserSelectOption extends BaseSelectOption {
  metaData?: UserMetadata;
}

export interface BuyerSelectOption extends BaseSelectOption {
  metaData?: BuyerMetadata;
}

export interface SupplierUserSelectOption extends BaseSelectOption {
  metaData?: UserMetadata & {
    supplier: true;
  };
}

export interface BuyerUserSelectOption extends BaseSelectOption {
  metaData?: UserMetadata & {
    supplier: false;
  };
}

// Delivery settings types
export enum CutOffTimeType {
  NO_CUTOFF = 'NoCutoff',
  SAME_DAY = 'SameDay',
  NEXT_DAY = 'NextDay',
  CUSTOM = 'Custom',
}

export interface CutOffTimeSetting {
  type: CutOffTimeType;
  time?: string; // Format: "HH:mm"
}

export interface WeekdayBasedCutoffSettings {
  mon: CutOffTimeSetting;
  tue: CutOffTimeSetting;
  wed: CutOffTimeSetting;
  thu: CutOffTimeSetting;
  fri: CutOffTimeSetting;
  sat: CutOffTimeSetting;
  sun: CutOffTimeSetting;
}

export interface DeliveryDay {
  day: string;
  enabled: boolean;
  cutoffSetting: CutOffTimeSetting;
}

// Sub-catalog types
export interface SubCatalogOption extends BaseSelectOption {
  supplierId: string;
  description?: string;
}

// Main form data types
export interface ChatParticipantsFormData {
  buyer: BuyerSelectOption | null;
  supplier: UserSelectOption | null;
  buyerUsers: BuyerUserSelectOption[];
  supplierUsers: SupplierUserSelectOption[];
  deselectedOrderPreferences: string[];
}

export interface ChatSettingsFormData {
  chatName: string;
  restaurantName: string;
  deliveryAddress: string;
  customerNumber: string;
  additionalEmails: string;
  chatType: ChatTypeOption | null;
  subCatalog: SubCatalogOption[];
  createdContext: CreatedContextOption | null;
  customerLevelMovAmount?: string | null;
  customerLevelMovCurrency?: string | null;
  visibleChatPrices?: boolean;
  isPriceOverrideEnabled: boolean;
}

export interface ChatDeliverySettingsFormData {
  hasCustomChatDeliverySettings: boolean;
  deliveryDays: DeliveryDay[];
  cutoffType: CutOffTimeType;
  cutoffTimes?: WeekdayBasedCutoffSettings;
}

// Combined form data
export interface CreateChatFormData
  extends ChatParticipantsFormData,
    ChatSettingsFormData,
    ChatDeliverySettingsFormData {}

// API input types (what gets sent to the server)
export interface CreateChatInput {
  chatName?: string | null;
  restaurantName?: string | null;
  deliveryAddress?: string | null;
  customerNumber?: string | null;
  additionalEmails?: string[];
  type: CreateChatType;
  supplierId: string;
  buyerId?: string;
  userIds: string[];
  subCatalogId?: string | null;
  isPuppy?: boolean;
  createdContext: CreatedContext;
  customerLevelMovAmount?: string | null;
  customerLevelMovCurrency?: string | null;
}

// Response types
export interface CreatedChatResponse {
  id: string;
  chatName?: string;
  restaurantName?: string;
  type: CreateChatType;
  enabled: boolean;
  createdAt: string;
  buyer?: {
    id: string;
    name: string;
  };
  supplier?: {
    id: string;
    name: string;
  };
}

// Hook configuration types
export interface CreateChatConfig {
  onSuccess?: (chat: CreatedChatResponse) => void;
  onError?: (error: Error) => void;
  captureException?: (error: Error) => void;
}

// Search and validation types
export interface ChatValidationRules {
  customerNumberMandatory: boolean;
  requiresBuyerUsers: boolean;
  requiresSupplierUsers: boolean;
  minDeliveryDays: number;
}

export interface CreateChatSearchParams {
  buyerSearchTerm?: string;
  supplierSearchTerm?: string;
  userSearchTerm?: string;
  isPuppy?: boolean;
}

// State management types
export interface CreateChatState {
  currentStep: number;
  isLoading: boolean;
  error: string | null;
  formData: Partial<CreateChatFormData>;
  validationRules: ChatValidationRules;
}

// Navigation and routing types
export interface CreateChatRouteParams {
  duplicateFromChatId?: string;
  buyerId?: string;
  supplierId?: string;
}

export interface CreateChatNavigationOptions {
  redirectTo?: string;
  openInNewTab?: boolean;
  preserveFormData?: boolean;
}
